// Currently it doensn't have any component here
// only functions are used now

import yaml from 'js-yaml';

export const loadCatalog = async (url) => {
  try {
    const response = await fetch(url); // Fetch the YAML file from the URL
    if (!response.ok) {
      throw new Error('Failed to load catalog');
    }
    const yamlText = await response.text(); // Read the response as text
    const parsedCatalog = yaml.load(yamlText); // Parse the YAML text into an object
    let updatedCatalog = updateCatalog(parsedCatalog, url);

    try {
      updatedCatalog = await updateFactorKeys(
        updatedCatalog, updatedCatalog.bucket);
      updatedCatalog = await updateBasemapKeys(
        updatedCatalog, updatedCatalog.bucket);
      updatedCatalog = removeIrrelevantHillshades(updatedCatalog);
      return updatedCatalog
    } catch (error) {
      console.error('Error updating factor keys:', error);
    }
  } catch (error) {
    console.error('Error loading catalog:', error);
  }
};

const removeIrrelevantHillshades = (catalog) => {
  try {
    // analysis id is catalog.id
    const analysisId = catalog.id.toLowerCase();
    // create a deep copy of the catalog instead of reference
    const newCatalog = JSON.parse(JSON.stringify(catalog));
    // remove irrelevant basemaps
    newCatalog.assets.basemap = newCatalog.assets.basemap?.filter(item => {
      // the url should include three parts: domain (S3 bucket), path (chip id), and file
      const urlParts = item?.replace("https://", "")?.split("/");
      // chip id
      const chipId = urlParts?.[1]?.toLowerCase();
      // file name
      const fileName = urlParts?.[urlParts.length - 1]?.toLowerCase();
      // the new catalog unique id all starts with a 'n'
      if (chipId?.startsWith("n")) {
        // only filter hillshade or sge generated images
        if (fileName?.includes("hillshade") || fileName?.includes("-sge") || fileName?.includes(".sge")) {
          // filter out the file other than current analysis
          if (!fileName?.includes(analysisId)) {
            return false;
          }
        }
      }
      return true;
    });
    return newCatalog;
  } catch (error) {
    console.log("Error removing irrelevant hillshade or sge layers from basemap.");
    return catalog;
  }
}

const updateCatalog = (parsedCatalog, catalogUrl) => {

  const documentRoot = catalogUrl.substring(0, catalogUrl.lastIndexOf('/') + 1);
  const hostname = new URL(catalogUrl).hostname;
  const bucket = parsedCatalog.bucket || hostname.split('.')[0];

  const updateTilePaths = (background) => {
    if (Array.isArray(background)) {
      return background.map((item) => {
        const updatedTilePaths = item.tilePaths.map((tilePath) => {
          // Check if the tilePath is relative and combine it with documentRoot
          if (!tilePath.startsWith('http') && !tilePath.startsWith('/')) {
            return `${documentRoot}${tilePath}`;
          }
          return tilePath;
        });
        return { ...item, tilePaths: updatedTilePaths };
      });
    }
    return background;
  };

  const updatedSGE = {
    ...parsedCatalog.assets.sge,
    fullDepth: `${documentRoot}${parsedCatalog.assets.sge.fullDepth}`,
    perGene: `${documentRoot}${parsedCatalog.assets.sge.perGene}`,
    // Handle the stat property
    stat: parsedCatalog.assets.sge.stat.endsWith('sge_count.json')
      ? `${documentRoot}${parsedCatalog.assets.sge.stat}`
      : `${documentRoot}${parsedCatalog.assets.sge.stat}sge_count.json`,
  };

  const updatedCatalog = {
    ...parsedCatalog,
    documentRoot: documentRoot,
    bucket: bucket,
    assets: {
      ...parsedCatalog.assets,
      background: updateTilePaths(parsedCatalog.assets.background),
      sge: updatedSGE,
    },
  };

  return updatedCatalog;
};

const getObjectKeysByExtension = async (bucket, folder, extension) => {
  try {
    // Construct the API endpoint URL
    const apiUrl = `https://htsz8cbyj8.execute-api.us-east-1.amazonaws.com/dev/lists3?mode=object&bucket=${bucket}&folder=${folder}`;
    const host = `https://${bucket}.s3.amazonaws.com`;

    const response = await fetch(apiUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch object list');
    }
    const data = await response.json();
    const filteredKeys = data
      .filter((f) => f.endsWith(`.${extension}`))
      .map((f) => `${host}/${f}`);
    return filteredKeys;
  } catch (error) {
    console.error('Error fetching object keys:', error);
    throw error; // Propagate the error for handling at a higher level
  }
}

// Function to update assets.factor in the catalog
const updateFactorKeys = async (catalog, bucket) => {
  const factorExtensions = {
    DE: 'tsv',
    hexagon: 'pmtiles',
    pixel: 'tif',
  };

  // Create a copy of the catalog to avoid modifying the original
  const updatedCatalog = { ...catalog };

  // Iterate through the factor keys and fetch S3 keys for each
  for (const factorKey in updatedCatalog.assets.factor) {
    if (factorExtensions.hasOwnProperty(factorKey)) {
      const folderPath = updatedCatalog.assets.factor[factorKey];
      const extension = factorExtensions[factorKey];

      try {
        const factorKeys = await getObjectKeysByExtension(bucket, folderPath, extension);
        updatedCatalog.assets.factor[factorKey] = factorKeys;
      } catch (error) {
        console.error(`Error fetching S3 keys for ${factorKey}:`, error);
        // Handle error as needed
      }
    }
  }
  return updatedCatalog;
}

// Function to update assets.basemp in the catalog
const updateBasemapKeys = async (catalog, bucket) => {

  // Create a copy of the catalog to avoid modifying the original
  const updatedCatalog = { ...catalog };

  let folderPath;
  const basemapPath = updatedCatalog.assets.basemap;
  const extension = 'pmtiles';

  // Check if basemapPath starts with 's3://'
  if (basemapPath.startsWith('s3://')) {
    // Extract the bucket and folder path from basemapPath
    const pathParts = basemapPath.slice(5).split('/', 1); // Remove 's3://' and split
    bucket = pathParts[0]; // The new bucket name
    folderPath = basemapPath.slice(5 + bucket.length + 1); // The remaining part becomes the folderPath
  } else {
    // Use the existing bucket parameter and basemapPath as folderPath
    folderPath = basemapPath;
  }

  try {
    const basemapKeys = await getObjectKeysByExtension(bucket, folderPath, extension);
    updatedCatalog.assets.basemap = basemapKeys;
  } catch (error) {
    console.error(`Error fetching S3 keys for basemap:`, error);
    // Handle error as needed
  }
  return updatedCatalog;
}


export const loadCatalogAsIs = async (yamlText) => {
  try {
    const parsedCatalog = yaml.load(yamlText);
    console.log(parsedCatalog);
    return parsedCatalog
  } catch (error) {
    console.error('Error loading catalog:', error);
  }
};

